import type {
  ITelemetryBaseEvent,
  ITelemetryBaseLogger,
} from "@fluidframework/azure-client";

import { recordErrorInSentry } from "../../utils/init-sentry.js";

const REPORT_COOLDOWN_MS = 5 * 60 * 1000;
const CONNECTION_EVENT_NAME = /join|connect|delta|fetchmissingdeltas/iu;
const DIAGNOSTIC_FIELDS = [
  "docId",
  "containerId",
  "mode",
  "duration",
  "attempts",
  "statusCode",
  "errorType",
  "online",
  "containerConnectionState",
  "lastQueuedSequenceNumber",
  "lastProcessedSequenceNumber",
  "lastObserved",
  "pendingOps",
  "inboundLength",
  "inboundPaused",
  "dmInitialSeqNumber",
  "dmLastKnownSeqNumber",
  "connectionStateDuration",
  "loaderVersion",
] as const;
const JOIN_DETAIL_FIELDS = [
  "protocolInitialized",
  "pendingClientId",
  "clientJoined",
  "waitingForLeaveOp",
] as const;

const isDiagnosticValue = (
  value: unknown
): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const getDiagnostics = (event: ITelemetryBaseEvent) => {
  const diagnostics: Record<string, string | number | boolean> = {};

  for (const field of DIAGNOSTIC_FIELDS) {
    const value = event[field];
    if (isDiagnosticValue(value)) {
      diagnostics[field] = value;
    }
  }

  if (typeof event.details === "string") {
    try {
      const details: unknown = JSON.parse(event.details);
      if (details && typeof details === "object") {
        for (const field of JOIN_DETAIL_FIELDS) {
          const value = (details as Record<string, unknown>)[field];
          if (isDiagnosticValue(value)) {
            diagnostics[field] = value;
          }
        }
      }
    } catch {
      return diagnostics;
    }
  }

  return diagnostics;
};

export const createFluidTelemetryLogger = (
  teamId: string | null
): ITelemetryBaseLogger => {
  const lastReportedAt = new Map<string, number>();

  return {
    send(event) {
      const eventName = event.eventName.split(":").at(-1) ?? event.eventName;
      if (
        event.category !== "error" ||
        !CONNECTION_EVENT_NAME.test(eventName)
      ) {
        return;
      }

      const documentId =
        typeof event.docId === "string" ? event.docId : "unknown";
      const reportKey = `${documentId}:${eventName}`;
      const now = Date.now();
      if (
        now - (lastReportedAt.get(reportKey) ?? -Infinity) <
        REPORT_COOLDOWN_MS
      ) {
        return;
      }

      lastReportedAt.set(reportKey, now);
      recordErrorInSentry(new Error(`Fluid connection error: ${eventName}`), {
        extra: {
          ...getDiagnostics(event),
          teamId,
        },
        tags: {
          event: eventName,
          feature: "fluid",
          fluidDocumentId: documentId,
        },
      });
    },
  };
};
